import { memo, useCallback } from 'react'

import { GppGoodOutlined, GppMaybeOutlined } from '@mui/icons-material'
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { format } from 'date-fns'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { DATE_TIME_FORMAT } from '@shared/utils/support'
import { useEventUpdateMutation } from 'hooks/mutations/smep'

export type VerificationProps = {
  event: SmepEvent
}

const VerificationRaw = ({ event }: VerificationProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: updateEvent, isPending } = useEventUpdateMutation()

  /**
   * The platform resolves the verifier from their account, but only best effort — an account with no
   * name at all leaves the email, and a lookup that failed leaves just the id.
   */
  const verifierName = event.verifiedByName || event.verifiedByEmail || event.verifiedByUserId

  const handleSetVerified = useCallback(
    (verified: boolean) => {
      updateEvent(
        { eventId: event.id, smepEventEdit: { verified } },
        {
          onSuccess: () => {
            enqueueSnackbar(
              verified
                ? t('domain:Workspace.Event.Verification.verified')
                : t('domain:Workspace.Event.Verification.verificationRemoved'),
              { variant: 'success' },
            )
          },
          // Failures are left to the global handler, which surfaces the API's own reason.
        },
      )
    },
    [enqueueSnackbar, event.id, t, updateEvent],
  )

  return (
    <Paper sx={{ px: 4, py: 3, flex: 1, minWidth: 320 }} elevation={0}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        {event.verified ? (
          <GppGoodOutlined sx={{ color: (theme) => theme.palette.success[theme.palette.mode] }} />
        ) : (
          <GppMaybeOutlined sx={{ color: 'text.secondary' }} />
        )}
        <Typography variant="heading-md">{t('domain:Workspace.Event.Verification.title')}</Typography>
      </Stack>

      {event.verified ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 0.5 }}>
          <Typography variant="text">
            {t('domain:Workspace.Event.Verification.verifiedBy', { name: verifierName })}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }} variant="text-sm">
            {/* The email earns its place next to the name: a sign-off is the thing you go and ask about. */}
            {[
              !!event.verifiedTime && format(new Date(event.verifiedTime), DATE_TIME_FORMAT),
              event.verifiedByName && event.verifiedByEmail,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ color: 'text.secondary' }} variant="text-sm">
          {t('domain:Workspace.Event.Verification.description')}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        {event.verified ? (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            disabled={isPending}
            onClick={() => handleSetVerified(false)}
          >
            {t('domain:Workspace.Event.Verification.remove')}
          </Button>
        ) : (
          <Tooltip title={t('domain:Workspace.Event.Verification.droppedOnEditHint')}>
            <span>
              <Button size="small" variant="contained" disabled={isPending} onClick={() => handleSetVerified(true)}>
                {t('domain:Workspace.Event.Verification.verify')}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Paper>
  )
}

export const Verification = memo(VerificationRaw)
